/**
 * Formats currency input by automatically adding dollar sign and handling numeric input with proper comma formatting
 * @param value - The input value from the user
 * @returns Formatted currency string with dollar sign and commas
 */
export const formatCurrency = (value: string): string => {
  // Remove all non-numeric characters except decimal points
  let numericValue = value.replace(/[^\d.]/g, '');
  
  // If the field is empty, return empty string
  if (!numericValue) {
    return '';
  }
  
  // Handle decimal places (only allow up to 2 decimal places)
  const parts = numericValue.split('.');
  if (parts.length > 2) {
    numericValue = parts[0] + '.' + parts[1];
  }
  if (parts[1] && parts[1].length > 2) {
    numericValue = parts[0] + '.' + parts[1].substring(0, 2);
  }
  
  // Format with commas for thousands
  const [integerPart, decimalPart] = numericValue.split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // Combine integer and decimal parts
  const formattedValue = decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  
  // Add dollar sign
  return `$${formattedValue}`;
};

/**
 * Validates if a currency string is valid
 * @param value - The currency string to validate
 * @returns boolean indicating if the currency format is valid
 */
export const isValidCurrency = (value: string): boolean => {
  if (!value) return false;
  
  // Remove dollar sign for validation
  const numericPart = value.replace('$', '');
  
  // Check if it contains only numbers, commas, and at most one decimal point
  const currencyRegex = /^\d{1,3}(,\d{3})*(\.\d{0,2})?$|^\d+(\.\d{0,2})?$/;
  return currencyRegex.test(numericPart);
};